import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const STRATEGY_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type StrategyTranslatableField = (typeof STRATEGY_TRANSLATABLE_FIELDS)[number];
export type StrategyTranslations = TranslatableText<StrategyTranslatableField>;

export const strategyQuerySchema = type({
  'name?': 'string',
});

export type StrategyQuery = typeof strategyQuerySchema.t;

/**
 * Strategy data schema.
 * Contains configurable settings for a strategy, including working days.
 */
/**
 * ISO weekday number (0=Monday through 6=Sunday).
 */
export const isoWeekdaySchema = type('0 | 1 | 2 | 3 | 4 | 5 | 6');

export type IsoWeekday = typeof isoWeekdaySchema.t;

export const solverWeightsSchema = type({
  balancePreference: 'number>=0',
  floaterPenalty: 'number>=0',
  skillMatchQuality: 'number>=0',
  fairness: 'number>=0',
}).configure({
  description:
    'Per-constraint multipliers used by the solver. Higher = the solver works harder to minimise that penalty. `0` disables the constraint. See SOLV-5.',
});

export const strategyDataSchema = type({
  'workingDays?': isoWeekdaySchema.array().configure({
    description:
      'Working days as ISO weekday numbers (0=Monday … 6=Sunday). Days not listed are treated as closed. Defaults to Mon–Fri ([0,1,2,3,4]).',
    examples: [[0, 1, 2, 3, 4]],
  }),
  'plannerApprovalRequired?': type('boolean').configure({
    description:
      'When true, swap/takeover requests using plans with this strategy require an extra planner approval after the target employee accepts.',
    examples: [false],
  }),
  'solverWeights?': solverWeightsSchema.configure({
    description:
      'Per-tenant solver constraint weights (SOLV-5). When omitted, the solver uses `DEFAULT_SOLVER_WEIGHTS` from `@zag/planning/common`.',
  }),
}).configure({ description: 'Configurable settings for a planning strategy (working days, approval workflow toggles, solver weights).' });

export type StrategyData = Omit<typeof strategyDataSchema.t, 'workingDays'> & {
  workingDays?: IsoWeekday[];
};

/**
 * Schema for creating a new strategy.
 */
export const createStrategySchema = type({
  'name?': type('string>0').configure({
    description: 'Display name of the strategy. Optional if `translations.en.name` is supplied.',
    examples: ['Mon-Fri office', '24/7 care'],
  }),
  'description?': type('string | null').configure({
    description: 'Free-form description. `null` clears the value.',
    examples: ['Standard Monday to Friday office strategy.'],
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier. `null` clears the icon.',
    examples: ['event'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS accent colour, typically a six-digit hex. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'URL to an illustrative image. `null` clears the image.',
  }),
  'data?': strategyDataSchema.or('null').configure({
    description: 'Strategy settings (working days, approval workflow). `null` resets to defaults.',
  }),
  'translations?': translatableTextSchema(STRATEGY_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload for creating a new planning strategy.' });

export type CreateStrategy = typeof createStrategySchema.t;

/**
 * Schema for updating an existing strategy.
 */
export const updateStrategySchema = type({
  'name?': type('string>0').configure({
    description: 'Updated display name.',
    examples: ['Mon-Fri office'],
  }),
  'description?': type('string | null').configure({
    description: 'Updated description. `null` clears the value.',
  }),
  'icon?': type('string | null').configure({
    description: 'Updated Material Icons identifier. `null` clears the icon.',
    examples: ['event'],
  }),
  'color?': type('string | null').configure({
    description: 'Updated accent colour. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Updated image URL. `null` clears the image.',
  }),
  'data?': strategyDataSchema.or('null').configure({
    description: 'Updated strategy settings. `null` resets to defaults.',
  }),
  'translations?': translatableTextSchema(STRATEGY_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({ description: 'Partial update of a strategy — fields omitted from the body remain unchanged.' });

export type UpdateStrategy = typeof updateStrategySchema.t;

/**
 * Default working days: Monday through Friday (ISO weekday 0-4).
 */
export const DEFAULT_WORKING_DAYS: number[] = [0, 1, 2, 3, 4];

/**
 * Check if a weekday is a working day based on the strategy's working days config.
 * @param weekday - ISO weekday number (0=Monday, 6=Sunday)
 * @param workingDays - Array of working day numbers (defaults to Mon-Fri)
 * @returns true if the weekday is a working day
 */
export function isWorkingDay(weekday: number, workingDays: number[] = DEFAULT_WORKING_DAYS): boolean {
  return workingDays.includes(weekday);
}

/**
 * Convert JavaScript day (0=Sunday, 6=Saturday) to ISO weekday (0=Monday, 6=Sunday).
 */
export function jsWeekdayToIso(jsDay: number): number {
  return jsDay === 0 ? 6 : jsDay - 1;
}

/**
 * Check if a JavaScript Date falls on a working day.
 *
 * @param date - JavaScript Date object. Must represent local midnight (e.g. from new Date())
 *   NOT a UTC-midnight date parsed from an ISO string (new Date('YYYY-MM-DD') is UTC midnight
 *   and would give the wrong weekday in non-UTC timezones via getDay()).
 *   For ISO date strings, use isDateKeyWorkingDay() instead.
 * @param workingDays - Array of ISO working day numbers (defaults to Mon-Fri)
 * @returns true if the date is a working day
 */
export function isDateWorkingDay(date: Date, workingDays: number[] = DEFAULT_WORKING_DAYS): boolean {
  const isoWeekday = jsWeekdayToIso(date.getDay());
  return isWorkingDay(isoWeekday, workingDays);
}

/**
 * Response type for Strategy entity from API.
 * Includes computed fields like counts.
 */
export interface StrategyResponse {
  id: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
  data: StrategyData;
  createdAt: string;
  updatedAt: string;
  /** Number of shift definitions using this strategy */
  shiftDefinitionsCount?: number;
  /** Number of plans using this strategy */
  plansCount?: number;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
}
